/**
 * Safe context for Supabase FunctionsHttpError events.
 *
 * The SDK error deliberately exposes a generic message, while its `context`
 * is a Response that may carry a full URL and a response body.  Sentry needs
 * the function slug and status to diagnose a failed call, but it must never
 * receive the URL, query string, request body, or account identifiers.
 *
 * This module is deliberately framework-free so the native and web SDKs use
 * the same bounded extraction in `beforeSend`.
 */

export interface SafeEdgeFunctionFailureContext {
  edge_function?: string
  http_status?: number
  upstream_code?: string
}

type UnknownRecord = Record<string, unknown>

const FUNCTION_SLUG = /^[a-z][a-z0-9_-]{0,79}$/i
const OUTCOME_CODE = /^[A-Z][A-Z0-9_]{1,79}$/
const MAX_CAUSE_DEPTH = 4

function asRecord(value: unknown): UnknownRecord | null {
  return value && typeof value === 'object' ? (value as UnknownRecord) : null
}

// Response-style accessors are useful, but an arbitrary error may also carry
// a throwing getter. Diagnostics must not introduce a second SDK failure.
function read(record: UnknownRecord | null, key: string): unknown {
  try {
    return record?.[key]
  } catch {
    return undefined
  }
}

function validStatus(value: unknown): number | undefined {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && /^[1-5]\d{2}$/.test(value)
        ? Number(value)
        : NaN
  return Number.isInteger(parsed) && parsed >= 100 && parsed <= 599 ? parsed : undefined
}

function safeFunctionSlug(value: unknown): string | undefined {
  if (typeof value !== 'string' || value.length > 2048) return undefined
  try {
    const pathname = new URL(value).pathname
    const match = pathname.match(/^\/functions\/v1\/([^/]+)$/)
    const slug = match?.[1]
    return slug && FUNCTION_SLUG.test(slug) ? slug : undefined
  } catch {
    return undefined
  }
}

function safeOutcomeCode(value: unknown): string | undefined {
  return typeof value === 'string' && OUTCOME_CODE.test(value) ? value : undefined
}

function isEdgeFunctionHttpError(item: UnknownRecord): boolean {
  const name = read(item, 'name')
  const code = read(item, 'code')
  if (
    name === 'FunctionsHttpError' ||
    name === 'VerificationHttpError' ||
    code === 'FUNCTIONS_HTTP_ERROR' ||
    code === 'VERIFICATION_HTTP_ERROR'
  ) {
    return true
  }
  const message = read(item, 'message')
  return typeof message === 'string' && /Edge Function returned a non-2xx/i.test(message)
}

/**
 * ServiceError preserves the native FunctionsHttpError under `cause`. Walk
 * only a short, cycle-safe chain so the common service-layer capture retains
 * the original Response getters without treating arbitrary nested errors as
 * Edge Function telemetry.
 */
function edgeFunctionErrorChain(error: unknown): UnknownRecord[] {
  const chain: UnknownRecord[] = []
  const seen = new Set<object>()
  let current = error

  for (let depth = 0; depth < MAX_CAUSE_DEPTH; depth += 1) {
    const item = asRecord(current)
    if (!item || seen.has(item)) break
    seen.add(item)
    chain.push(item)
    current = read(item, 'cause')
  }

  return chain
}

/**
 * Extract strictly bounded correlation fields from a FunctionsHttpError.
 * Returns an empty object for every other error type, even when it happens to
 * contain an arbitrary `url`, `status`, or `code` property.
 */
export function safeEdgeFunctionFailureContext(error: unknown): SafeEdgeFunctionFailureContext {
  const chain = edgeFunctionErrorChain(error)
  const functionErrorIndex = chain.findIndex(isEdgeFunctionHttpError)
  if (functionErrorIndex === -1) return {}

  const status = chain.reduce<number | undefined>((result, item) => {
    if (result !== undefined) return result
    const details = asRecord(read(item, 'details'))
    return (
      validStatus(read(asRecord(read(item, 'context')), 'status')) ??
      validStatus(read(details, 'upstreamStatus')) ??
      validStatus(read(item, 'status')) ??
      validStatus(read(item, 'statusCode'))
    )
  }, undefined)
  const functionName = chain
    .slice(functionErrorIndex)
    .map((item) => safeFunctionSlug(read(asRecord(read(item, 'context')), 'url')))
    .find((value): value is string => value !== undefined)
  const upstreamCode = chain.reduce<string | undefined>((result, item) => {
    if (result !== undefined) return result
    const details = asRecord(read(item, 'details'))
    const code = read(item, 'code')
    return (
      safeOutcomeCode(read(details, 'upstreamCode')) ??
      (code === 'FUNCTIONS_HTTP_ERROR' || code === 'VERIFICATION_HTTP_ERROR'
        ? undefined
        : safeOutcomeCode(code))
    )
  }, undefined)

  return {
    ...(functionName ? { edge_function: functionName } : {}),
    ...(status !== undefined ? { http_status: status } : {}),
    ...(upstreamCode ? { upstream_code: upstreamCode } : {}),
  }
}

/** Minimal structural event shape shared by @sentry/react and react-native. */
export interface EdgeContextSentryEvent {
  tags?: Record<string, unknown>
  extra?: Record<string, unknown>
}

/** Add only the safe failure fields to an outgoing Sentry event. */
export function attachSafeEdgeFunctionFailureContext(
  event: EdgeContextSentryEvent,
  error: unknown,
): void {
  const context = safeEdgeFunctionFailureContext(error)
  if (context.edge_function || context.http_status !== undefined) {
    event.tags = {
      ...(event.tags ?? {}),
      ...(context.edge_function ? { edge_function: context.edge_function } : {}),
      ...(context.http_status !== undefined ? { http_status: String(context.http_status) } : {}),
    }
  }
  if (context.upstream_code) {
    event.extra = { ...(event.extra ?? {}), upstream_code: context.upstream_code }
  }
}
